import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Container, Skeleton, Stack, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Token } from 'entities/Token';

import Slider from 'shared/components/Slider';
import NFTCard from 'shared/components/Cards/NFT';
import PrivateDialog from 'shared/dialogs/Private';
import EmptyPage from 'shared/components/EmptyPage';

import CheckAccessHoc from 'hoc/CheckPermissions';

import { ProfileActions } from 'store/reducers/profile';
import { TokenActions } from 'store/reducers/token';
import { WalletActions } from 'store/reducers/wallet';
import { EmailActions } from 'store/reducers/email';

import Sports from './blocks/Sports';
import Creators from './blocks/Creators';
import Heading from './blocks/Heading';
import { Banner1, Banner2, Banner3 } from './banners';

import { useStyles } from './styles';

export default function Home() {
  const dispatch = useDispatch();
  const { classes } = useStyles();
  const theme = useTheme();

  const [bannersAutoplay, setBannersAutoplay] = useState<boolean>(true);

  // Selectors
  const {
    hotTokens: { hotTokens },
    latestTokens: { latestTokens, loading: loadingLatestTokens },
  } = useSelector((state) => state.token);
  const {
    wallet: { wallet },
    popups: { noRightsModalOpen, currentTokenCreatorId },
    profile: { profile },
    email: {
      joinEmail: { joinModalIsOpen },
    },
  } = useSelector((state) => state);

  useEffect(() => {
    dispatch(TokenActions.hotTokens.getHotTokensRequest());
    dispatch(TokenActions.latestTokens.getLatestTokensRequest());
  }, []);

  useEffect(() => {
    if (currentTokenCreatorId) {
      dispatch(ProfileActions.getProfileRequest({ id: currentTokenCreatorId }));
    }
  }, [currentTokenCreatorId]);

  useEffect(() => {
    if (joinModalIsOpen) {
      setBannersAutoplay(false);
    } else {
      setBannersAutoplay(true);
    }
  }, [joinModalIsOpen]);

  const handleConnect = () => {
    dispatch(WalletActions.openModal());
  };

  const handleJoin = () => {
    dispatch(EmailActions.joinEmail.toggleJoinModal({ open: true }));
  };

  const hotTokensStack = useMemo(
    () =>
      hotTokens.length &&
      hotTokens.map((item: Token) => {
        return (
          <CheckAccessHoc
            creatorId={item.creator?.id}
            creatorUserName={item.creator?.nickName || ''}
            isSecondary={item.isSecondary}
            isExclusive={item.exclusive}
            key={item.id}
          >
            <NFTCard
              data={item}
              username={item.username}
              price={item.price}
              title={item.title}
              exclusive={item.exclusive}
              previewImg={item.image}
              blockchain={item.blockchain}
              amountOfCopiesList={item.onSaleAmountOfCopies}
              collection={item.collection}
              isSecondary={item.isSecondary}
            />
          </CheckAccessHoc>
        );
      }),
    [hotTokens],
  );

  const SkeletonStack = () => {
    let count: number = 1;
    if (useMediaQuery(theme.breakpoints.up('sm'))) {
      count = 2;
    }
    if (useMediaQuery(theme.breakpoints.up('lg'))) {
      count = 4;
    }
    return (
      <Stack direction="row" spacing={4}>
        {[...Array(count)].map((e: any, i: number) => (
          <Skeleton key={i} variant="rectangular" width={286} height={391} />
        ))}
      </Stack>
    );
  };

  const bannerSliderSettings = {
    slidesToShow: 1,
    slidesToScroll: 1,
    infinite: true,
    autoplay: bannersAutoplay,
    autoplaySpeed: 5000,
    responsive: [
      {
        breakpoint: 1536,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
          dots: true,
        },
      },
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
          dots: true,
        },
      },
      {
        breakpoint: 900,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
          dots: true,
        },
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
          dots: true,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
          dots: true,
        },
      },
    ],
  };

  const tokens = useMediaQuery(theme.breakpoints.between('xs', 'sm')) && latestTokens.length ? latestTokens.slice(0, 8) : latestTokens;

  const latestTokensStack = useMemo(() => {
    return (
      tokens.length &&
      tokens.map((item: Token) => (
        <CheckAccessHoc
          creatorId={item.creator?.id}
          creatorUserName={item.creator?.nickName || ''}
          isSecondary={item.isSecondary}
          isExclusive={item.exclusive}
          key={item.id}
        >
          <NFTCard
            data={item}
            username={item.username}
            price={item.price}
            title={item.title}
            exclusive={item.exclusive}
            previewImg={item.image}
            blockchain={item.blockchain}
            amountOfCopiesList={`${item.onSaleAmountOfCopies}/${item.amountOfCopies}`}
            collection={item.collection}
            isSecondary={item.isSecondary}
          />
        </CheckAccessHoc>
      ))
    );
  }, [tokens]);

  return (
    <>
      <Container>
        <Box mb={9} className={classes.mainSliderBlock}>
          <Slider disableLearn settings={bannerSliderSettings}>
            <Banner1 isConnected={!!wallet.address} handleConnect={handleConnect} />
            <Banner2 isConnected={!!wallet.address} handleConnect={handleConnect} />
            <Banner3 handleJoin={handleJoin} />
          </Slider>
        </Box>

        <Box mb={7}>
          <Sports />
        </Box>

        {hotTokens.length > 0 && (
          <Box mb={7}>
            <Heading label="Hot sales" />
            <Box mt={{ lg: 4 }}>
              <Slider>{hotTokensStack}</Slider>
            </Box>
          </Box>
        )}

        <Box mb={7}>
          <Heading label="Latest drops" />

          <Box mt={{ lg: 4 }}>
            {loadingLatestTokens ? (
              <SkeletonStack />
            ) : latestTokens.length > 0 ? (
              <Slider
                settings={{
                  draggable: false,
                }}
              >
                {latestTokensStack}
              </Slider>
            ) : (
              <EmptyPage title="No NFTs to display..." description="Once NFTs are added, you will see them here." />
            )}
          </Box>
        </Box>

        <Box>
          <Creators />
        </Box>
      </Container>
      <PrivateDialog open={noRightsModalOpen} celebrity={profile} />
    </>
  );
}
